import { Box, Button, Divider, Stack, Theme, Tooltip, alpha } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useBundleTranslation from 'i18n';
import parse from 'html-react-parser';
import { escapeLatexSpecial, processParts, parseMarkdownTable, stripHtmlTags } from 'app/concierge/tools';
import { mapConciergeFilters, buildGlobalSearchTarget, resolveSearchParams } from 'app/concierge/tools/search-mapper';
import { useDispatch, useSelector } from 'react-redux';
import { setMessageDomain } from 'store/conciergeSlice';
import IconHandler from 'components/common/icon/IconHandler';
import {
    ChartData,
    ConciergeSearchParams,
    JsonBlock,
    SearchResult,
    SearchResults,
    StatusBadge,
    TableData,
    HChart,
    IDialogItem,
    IMessagePart,
    ToolSelect,
    IDataSource,
    DomainId,
    LLMLogLinkType,
    AuthRequired as AuthRequiredType,
    ProceedButton as ProceedButtonType,
    ActionButtons as ActionButtonsType,
    NextSource,
    RelatedReports as RelatedReportsType,
    LearningUpdateRun as LearningUpdateRunType,
} from 'app/concierge/types';
import MarkdownIt from 'markdown-it';
import katex from 'katex';
import HtmlPart from './parts/HtmlPart';
import JsonStatusBadge from './parts/JsonStatusBadge';
import JsonSearchResults from './parts/JsonSearchResults';
import JsonTable from './parts/JsonTable';
import JsonChart from './parts/JsonChart';
import InvalidRequestPart from './parts/InvalidRequestPart';
import { AuthRequired } from './parts/AuthRequired';
import { ProceedButton } from './parts/ProceedButton';
import ActionButtons from './parts/ActionButtons';
import { useLocation } from 'react-router-dom';
import { setParamsApplied, setQueryString, setSearchFilters } from 'store/globalSearchSlice';
import { ICustomService, IDomain, ITool } from 'store/conciergeSlice';
import { useConfigConstantValue } from 'hooks/useConfigConstant';
import ToolSelector from './parts/ToolSelector';
import JsonHChart from './parts/JsonHChart';
import StreamThinkingBlock from './parts/StreamThinkingBlock';
import { escapeHtml } from 'tools/tools';
import DataSource from './parts/DataSource';
import RelatedReports from './parts/RelatedReports';
import { merge } from 'lodash';
import { ICustomStyles } from 'app/concierge/panel';
import defaultStyles from './styles';
import 'katex/dist/katex.min.css';
import { useMiNavigation } from 'hooks/useNavigation';
import LLMLogLink from 'app/concierge/panel/active-chat/message/parts/LLMLogLink';
import { ConciergeLoader } from 'components/common/icon/IconMiData';
import LearningUpdateRun from './parts/LearningUpdateRun';

const md = MarkdownIt();

const LATEX_IN_MESSAGE_REGEX = /\\\[\s*(.*?)\s*\\]/gm;
const INLINE_LATEX_IN_MESSAGE_REGEX = /\\\(\s*(.*?)\s*\\\)/gm;
const MD_LINK_REGEX = /^(.*?):(.*)?\[Link\]\(([^)]+)\)(.*)$/gm;
const MD_LINK_REPLACER = parse(`<a href="$3" target="_blank">$1</a>:<span>$2</span><br /><br />`) as string;
const MD_NOT_FULL_LINK_REGEX = /(\[([^\]]+)]\(((http)|\/|\.)[^)]+(?!=\)))$/i;
const MD_NOT_FULL_LINK_REPLACER = parse(`$2`) as string;

interface IProps {
    index: number;
    item: IDialogItem;
    dialog: IDialogItem[];
    setSelectedMessage: (index: number | null) => void;
    showFeedback: boolean;
    loading: boolean;
    showSearchRequest?: boolean;
    botIcon?: string;
    loadMore?: boolean;
    onUpdateMessage?: (index: number, updatedItem: IDialogItem) => void;
    onRemoveMessage?: (index: number) => void;
    onRemoveAuthMessages?: () => void;
    useInfoButton?: boolean;
    onRetryError?: () => void;
    onAction?: (
        action: string,
        payload: any,
    ) => Promise<{ success: boolean; message?: string }> | { success: boolean; message?: string };
}

function Message({
    index,
    item,
    dialog,
    setSelectedMessage,
    showFeedback,
    loading,
    showSearchRequest,
    botIcon,
    loadMore,
    onUpdateMessage,
    onRemoveMessage,
    onRemoveAuthMessages,
    useInfoButton,
    onRetryError,
    onAction,
}: IProps) {
    const { t } = useBundleTranslation(['app/concierge/widget']);

    const dispatch = useDispatch();
    const navigate = useMiNavigation();
    const location = useLocation();

    const onSearchPage = location.pathname.includes('/search/global');

    const availableTools = useSelector((state: any) => state.concierge.availableTools) as ITool[];
    const availableDomains = useSelector((state: any) => state.concierge.availableDomains) as IDomain[];
    const conciergeSettings = useSelector((state: any) => state.auth?.configuration?.conciergeSettings) ?? {};
    const customIconAvatar = useSelector((state: any) => state.auth?.userInfo?.customIcon);
    const selectedDomains = useSelector((state: any) => state.concierge.selectedDomains);
    const messageDomains = useSelector((state: any) => state.concierge.messageDomains) as { [key: string]: string };
    const showSearchRequestIcon =
        showSearchRequest !== undefined ? showSearchRequest : conciergeSettings.show_search_request_icon_ind === 'Y';
    const allowJS = !!useConfigConstantValue('ALLOW_JAVASCRIPT_IN_CONCIERGE_RESPONSE');
    const [messageCustomService, setMessageCustomService] = useState<ICustomService | null>(null);

    const selectedDomainsOnInit = item.parameters?.selectedDomains || selectedDomains;
    const customStyles = useSelector((state: any) => state.concierge.customStyles) as ICustomStyles;
    const isMobile = useSelector((state: any) => state.concierge.isMobile) as boolean;

    const styles = useMemo(() => {
        return merge({}, defaultStyles, { dialogItem: customStyles?.activeChatMessage }) as typeof defaultStyles;
    }, [customStyles]);

    const hasError = item.parts.some(
        (part) => part.type === 'json' && part.jsonBlocks?.some((block) => 'error' in block),
    );

    const isLastBotMessage = useMemo(() => {
        if (!loading || item.type !== 'response') return false;
        for (let i = dialog.length - 1; i >= 0; i--) {
            if (dialog[i].type === 'response') {
                return dialog[i].uid === item.uid;
            }
        }
        return false;
    }, [loading, item.type, item.uid, dialog]);

    const [showCheck, setShowCheck] = useState(false);
    const prevIsLastRef = useRef(false);
    const prevLoadingRef = useRef(loading);

    useEffect(() => {
        const wasLast = prevIsLastRef.current;
        const wasLoading = prevLoadingRef.current;
        prevIsLastRef.current = isLastBotMessage;
        prevLoadingRef.current = loading;
        if (wasLast && wasLoading && !loading && !hasError) {
            setShowCheck(true);
            const timer = setTimeout(() => setShowCheck(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [isLastBotMessage, loading]);

    useEffect(() => {
        if (loading) setShowCheck(false);
    }, [loading]);

    const iconProps = {
        type: botIcon !== undefined ? 'upload' : conciergeSettings.bot_icon_ind,
        value:
            botIcon !== undefined
                ? botIcon
                : conciergeSettings.bot_icon_ind === 'upload'
                  ? `data:image/png;base64,${conciergeSettings.bot_icon_data}`
                  : conciergeSettings.bot_icon_name,
    };

    const [showSettings, setShowSettings] = useState(false);
    const [badgeType, setBadgeType] = useState<string | null>(null);
    const [responseTool, setResponseTool] = useState<string | null>(null);
    const [message, setMessage] = useState<React.JSX.Element | null>(null);
    const [searchParams, setSearchParams] = useState<ConciergeSearchParams | null>(null);
    const [searchData, setSearchData] = useState<SearchResult[] | null>(null);
    const [summaryOnly, setSummaryOnly] = useState(false);
    const [sqlParams, setSqlParams] = useState('');

    useEffect(() => {
        const processedParts = processParts(item.parts);

        const getFilteredStatusBadges = (): StatusBadge[] => {
            const allStatusBadges = processedParts
                .filter((part) => part.type === 'json' && part.jsonBlocks?.length)
                .flatMap((part) => part.jsonBlocks!)
                .filter((json) => json?.data_type && json?.data_type === 'status-badge') as StatusBadge[];

            if (loading || allStatusBadges.length === 0) {
                return allStatusBadges;
            }

            let hasContentAfterLastBadge = false;

            let lastStatusBadgePartIndex = -1;
            for (let i = processedParts.length - 1; i >= 0; i--) {
                const part = processedParts[i];
                if (
                    part.type === 'json' &&
                    part.jsonBlocks?.some((json: any) => json?.data_type && json?.data_type === 'status-badge')
                ) {
                    lastStatusBadgePartIndex = i;
                    break;
                }
            }

            if (lastStatusBadgePartIndex !== -1) {
                for (let i = lastStatusBadgePartIndex + 1; i < processedParts.length; i++) {
                    const part = processedParts[i];

                    if (part.type === 'invalid-request') {
                        hasContentAfterLastBadge = true;
                        break;
                    } else if (part.type === 'markdown' && part.message && part.message.trim() !== '') {
                        hasContentAfterLastBadge = true;
                        break;
                    } else if (part.type === 'html' && part.message && part.message.trim() !== '') {
                        hasContentAfterLastBadge = true;
                        break;
                    } else if (part.type === 'json' && part.jsonBlocks?.length) {
                        const hasNonStatusBadgeJson = part.jsonBlocks.some(
                            (json: any) =>
                                json?.data_type &&
                                json.data_type !== 'status-badge' &&
                                json.data_type !== 'stream_thinking' &&
                                json.data_type !== 'next-source',
                        );
                        if (hasNonStatusBadgeJson) {
                            hasContentAfterLastBadge = true;
                            break;
                        }
                    }
                }
            }

            return hasContentAfterLastBadge ? allStatusBadges : allStatusBadges.slice(0, -1);
        };

        const allStatusBadges = getFilteredStatusBadges();

        for (let i = allStatusBadges.length - 1; i >= 0; i--) {
            const badge = allStatusBadges[i];
            if (badge.badge_type && badge.badge_type.trim() !== '') {
                setBadgeType(badge.badge_type);
                break;
            }
        }

        for (let i = allStatusBadges.length - 1; i >= 0; i--) {
            const badge = allStatusBadges[i];
            if (badge.tool && badge.tool.trim() !== '') {
                setResponseTool(badge.tool);
                break;
            }
        }

        setMessage(buildMessage(item));
    }, [JSON.stringify(item), loading]);

    const showInSearch = (
        searchParamsProp?: ConciergeSearchParams,
        searchDataProp?: SearchResult[],
        queryFallback?: string,
    ) => {
        const params = searchParamsProp || searchParams;
        const data = searchDataProp || searchData;
        const query = params?.query || queryFallback || '';

        if (!params && !query) {
            return;
        }

        const filters = params ? mapConciergeFilters(params, data) : {};

        dispatch(setSearchFilters(filters));
        dispatch(setQueryString(query));
        dispatch(setParamsApplied(false));

        if (!onSearchPage) {
            navigate(buildGlobalSearchTarget(query));
        }
    };

    const filterStatusBadgeParts = (parts: IMessagePart[]) => {
        const isBadgePart = (p: IMessagePart) =>
            p.type === 'json' &&
            p.jsonBlocks?.[0]?.data_type &&
            (p.jsonBlocks[0].data_type === 'status-badge' || p.jsonBlocks[0].data_type === 'domain-id');

        const isNextSourcePart = (p: IMessagePart) =>
            p.type === 'json' && p.jsonBlocks?.[0]?.data_type === 'next-source';

        const filterSection = (section: IMessagePart[]): IMessagePart[] => {
            let sectionParts = section;

            const statusBadgeParts = sectionParts.filter(isBadgePart);

            if (statusBadgeParts.length > 1) {
                const lastStatusBadge = statusBadgeParts[statusBadgeParts.length - 1];
                const firstBadgeIndex = sectionParts.findIndex(isBadgePart);
                const withoutBadges = sectionParts.filter((p) => !isBadgePart(p));
                sectionParts = [
                    ...withoutBadges.slice(0, firstBadgeIndex),
                    lastStatusBadge,
                    ...withoutBadges.slice(firstBadgeIndex),
                ];
            }

            const badgeIdxs = sectionParts.map((p, i) => (isBadgePart(p) ? i : -1)).filter((i) => i >= 0);
            const lastBadgeIdx = badgeIdxs.length ? badgeIdxs[badgeIdxs.length - 1] : -1;
            const hasRealAfter =
                lastBadgeIdx >= 0 &&
                sectionParts
                    .slice(lastBadgeIdx + 1)
                    .some(
                        (p) =>
                            p.type === 'invalid-request' ||
                            p.type === 'markdown' ||
                            p.type === 'html' ||
                            !(
                                p.type === 'json' &&
                                p.jsonBlocks?.[0]?.data_type &&
                                (p.jsonBlocks[0].data_type === 'status-badge' ||
                                    p.jsonBlocks[0].data_type === 'domain-id' ||
                                    p.jsonBlocks[0].data_type === 'stream_thinking' ||
                                    p.jsonBlocks[0].data_type === 'next-source')
                            ),
                    );

            return hasRealAfter ? sectionParts.filter((p) => !isBadgePart(p)) : [...sectionParts];
        };

        const hasNextSource = parts.some(isNextSourcePart);
        if (!hasNextSource) {
            return filterSection(parts);
        }

        const sections: IMessagePart[][] = [];
        const separators: IMessagePart[] = [];
        let current: IMessagePart[] = [];

        for (const part of parts) {
            if (isNextSourcePart(part)) {
                sections.push(current);
                separators.push(part);
                current = [];
            } else {
                current.push(part);
            }
        }
        sections.push(current);

        const result: IMessagePart[] = [];
        for (let i = 0; i < sections.length; i++) {
            result.push(...filterSection(sections[i]));
            if (i < separators.length) {
                result.push(separators[i]);
            }
        }

        return result;
    };

    const buildMessage = (item: IDialogItem) => {
        const filtered = filterStatusBadgeParts(item.parts);
        const processedParts = processParts(filtered);

        const segments: React.ReactNode[] = [];
        let pendingMarkdown: IMessagePart[] = [];
        let originalPartIndex = 0;

        processedParts.forEach((part, partIdx) => {
            const originalIndex = item.parts.findIndex(
                (originalPart, idx) =>
                    idx >= originalPartIndex &&
                    originalPart.type === part.type &&
                    originalPart.message === part.message,
            );

            if (originalIndex !== -1) {
                originalPartIndex = originalIndex;
            }

            switch (part.type) {
                case 'html':
                case 'markdown':
                    pendingMarkdown.push(part);
                    break;

                case 'invalid-request':
                    if (pendingMarkdown.length > 0) {
                        segments.push(drawMessage(pendingMarkdown, item.type));
                        pendingMarkdown = [];
                    }
                    segments.push(<InvalidRequestPart key={`invalid-${segments.length}`} message={part.message} />);
                    break;

                case 'json':
                    if (!part.jsonBlocks) return;

                    if (pendingMarkdown.length > 0) {
                        segments.push(drawMessage(pendingMarkdown, item.type));
                        pendingMarkdown = [];
                    }

                    part.jsonBlocks.forEach((block, jsonIndex) => {
                        try {
                            segments.push(drawJsonMessage(block, item, originalPartIndex, jsonIndex));
                        } catch (error) {
                            console.error('Error rendering JSON block:', error);
                            segments.push(<Box key={`error-${segments.length}`}>Error rendering content</Box>);
                        }
                    });
                    break;
            }

            originalPartIndex++;
        });

        if (pendingMarkdown.length > 0) {
            segments.push(drawMessage(pendingMarkdown, item.type));
        }

        return (
            <>
                {segments.map((seg, i) => (
                    <Box key={i}>{seg}</Box>
                ))}
            </>
        );
    };

    const getSearchParams = (searchParams: ConciergeSearchParams) => {
        const paramsKeys = Object.keys(searchParams).filter(
            (key) => !['query', 'command', 'search_intent'].includes(key),
        );

        return (
            <Stack>
                <Box>{`${t('request_parameters.query')}: ${stripHtmlTags(searchParams.query)}`}</Box>
                <Box>{paramsKeys.length > 0 ? `${t('request_parameters.parameters')}:` : ''}</Box>
                {paramsKeys.length > 0 && (
                    <Stack sx={{ marginLeft: '10px' }}>
                        {paramsKeys.map((key) => {
                            if (key === 'query' || !searchParams.hasOwnProperty(key)) {
                                return null;
                            }

                            return (
                                <Stack key={key}>
                                    <Box>{`${key}: ${stripHtmlTags(String(searchParams[key]))}`}</Box>
                                </Stack>
                            );
                        })}
                    </Stack>
                )}
            </Stack>
        );
    };

    const getConciergeResponse4Popup = (dialogItem: IDialogItem, partIdx: number) => {
        let conciergeResponseMarkdown = '';
        let lastJsonPartIdx = -1;

        for (let i = 0; i < partIdx; i++) {
            const part = dialogItem.parts[i];
            if (part.type === 'json') {
                lastJsonPartIdx = i;
            }
        }

        const startIdx = lastJsonPartIdx === -1 ? 0 : lastJsonPartIdx + 1;

        for (let i = startIdx; i <= partIdx; i++) {
            const part = dialogItem.parts[i];
            if (part.type === 'markdown' || part.type === 'html') {
                conciergeResponseMarkdown += part.message;
            }
        }

        if (!conciergeResponseMarkdown.trim()) {
            return null;
        }

        return (
            <Stack
                key={index}
                direction={'row'}
                sx={styles.dialogItem}
                className={getClassName()}
                onClick={
                    summaryOnly
                        ? () => {
                              showInSearch();
                              setSelectedMessage(index);
                          }
                        : undefined
                }
            >
                <Box sx={styles.dialogItemAvatar}>
                    <Box sx={styles.dialogItemAvatarUser}>
                        {item.type == 'response' ? (
                            <IconHandler icon={iconProps} sx={{ fontSize: '24px' }} />
                        ) : (
                            <IconHandler icon={userIcon} sx={{ fontSize: '24px' }} />
                        )}
                    </Box>
                </Box>
                {badgeType && item.type == 'response' && badgeType === 'Searching' && (
                    <Tooltip title={getSearchTooltip()}>{getToolIcon('search')}</Tooltip>
                )}
                {badgeType && item.type == 'response' && badgeType === 'AnalyzingDocumentation' && (
                    <Tooltip title={getDocsTooltip()}>{getToolIcon('documents')}</Tooltip>
                )}
                {badgeType && item.type == 'response' && badgeType === 'UsingMCP' && (
                    <Tooltip title={getToolTooltip()}>{getToolIcon()}</Tooltip>
                )}
                <Stack sx={{ minWidth: 0, width: '100%' }}>
                    {
                        <Box
                            sx={styles.dialogItemText.messageText}
                            dangerouslySetInnerHTML={{
                                __html: md.render(conciergeResponseMarkdown),
                            }}
                        />
                    }
                </Stack>
            </Stack>
        );
    };

    const updateSearchResult = (updatedSearchResult: SearchResults) => {
        if (!onUpdateMessage) return;

        let partIdx = -1;
        let jsonIdx = -1;

        item.parts.forEach((part, partIndex) => {
            if (part.type === 'json' && item.parts[partIndex].jsonBlocks) {
                const blockIdx = item.parts[partIndex].jsonBlocks.findIndex(
                    (block) => block.data_type === 'search_results',
                );
                if (blockIdx !== -1) {
                    partIdx = partIndex;
                    jsonIdx = blockIdx;
                }
            }
        });

        if (partIdx > -1 && jsonIdx > -1) {
            const updatedItem = { ...item };
            const updatedParts = [...updatedItem.parts];
            const updatedPart = { ...updatedParts[partIdx] };
            const updatedJsonBlocks = [...(updatedPart.jsonBlocks || [])];
            updatedJsonBlocks[jsonIdx] = updatedSearchResult;
            updatedPart.jsonBlocks = updatedJsonBlocks;
            updatedParts[partIdx] = updatedPart;
            updatedItem.parts = updatedParts;

            onUpdateMessage?.(index, updatedItem);
        }
    };

    const drawJsonMessage = (jsonResult: JsonBlock, dialogItem: IDialogItem, partIdx: number, jsonIdx: number = 0) => {
        try {
            if ('error' in jsonResult) {
                return (
                    <Stack spacing={1} sx={{ mb: 2 }}>
                        <Box
                            sx={styles.errorCodeBlock}
                            dangerouslySetInnerHTML={{ __html: md.render(jsonResult.error) }}
                        />
                        <Box sx={styles.errorText}>{t('error.something_went_wrong')}</Box>
                        {onRetryError && index === dialog.length - 1 && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={onRetryError}
                                startIcon={<IconMi icon="refresh-cw" />}
                                sx={styles.errorRetryButton}
                            >
                                {t('error.retry')}
                            </Button>
                        )}
                    </Stack>
                );
            }

            if (jsonResult.data_type === 'status-badge') {
                return <JsonStatusBadge statusBadge={jsonResult as StatusBadge} />;
            }

            if (jsonResult.data_type === 'learning-update-run') {
                return <LearningUpdateRun block={jsonResult as LearningUpdateRunType} />;
            }

            if (jsonResult.data_type === 'data_sources') {
                const dataSource = jsonResult as IDataSource;
                return <DataSource dataSource={dataSource} />;
            }

            if (jsonResult.data_type === 'related_reports') {
                const relatedReports = jsonResult as RelatedReportsType;
                return <RelatedReports data={relatedReports} />;
            }

            if (jsonResult.data_type === 'search_results') {
                const searchResult = jsonResult as SearchResults;
                setSearchParams(resolveSearchParams(searchResult) ?? null);
                setSearchData(searchResult.data);
                setResponseTool('search');
                setBadgeType('Searching');

                return (
                    <JsonSearchResults
                        searchResult={searchResult}
                        onShowInSearch={showInSearch}
                        setSelectedMessage={setSelectedMessage}
                        setSummaryOnly={setSummaryOnly}
                        showSearchRequestIcon={showSearchRequestIcon}
                        getSearchParams={getSearchParams}
                        onSearchPage={dialogItem.parameters?.requestPath?.includes('/search/global')}
                        loadMore={loadMore}
                        onUpdateSearchResult={updateSearchResult}
                        messageId={item.uid}
                        useInfoButton={useInfoButton}
                    />
                );
            }

            if (jsonResult.data_type === 'table') {
                const tableData = jsonResult as TableData;

                setSqlParams(tableData.sql || '');
                setBadgeType('UsingMCP');
                const conciergeResponse4Popup = getConciergeResponse4Popup(dialogItem, partIdx);

                return <JsonTable tableData={tableData} conciergeResponse={conciergeResponse4Popup} />;
            }

            if (jsonResult.data_type === 'chart') {
                const chartData = jsonResult as ChartData;

                setSqlParams(chartData.sql || '');
                setBadgeType('UsingMCP');

                const conciergeResponse4Popup = getConciergeResponse4Popup(dialogItem, partIdx);

                return <JsonChart chartData={chartData} conciergeResponse={conciergeResponse4Popup} />;
            }

            if (jsonResult.data_type === 'next-block') {
                return null;
            }

            if (jsonResult.data_type === 'next-source') {
                return (
                    <Divider
                        key={`divider-${partIdx}`}
                        sx={{
                            my: 1.5,
                            borderStyle: 'dashed',
                            borderColor: (theme) => alpha(theme.palette.text.primary, 0.24),
                        }}
                    />
                );
            }

            if (jsonResult.data_type === 'domain-id') {
                const domainId = jsonResult as DomainId;

                setMessageCustomService({
                    name: domainId.tool,
                    domain_id: domainId.domain_id,
                });

                let lastStatusBadge = undefined;
                for (const part of dialogItem.parts) {
                    if (part.jsonBlocks) {
                        const statusBadges = part.jsonBlocks.filter((block) => block.data_type === 'status-badge');
                        if (statusBadges.length > 0) {
                            lastStatusBadge = statusBadges[statusBadges.length - 1];
                        }
                    }
                }

                if (lastStatusBadge) {
                    return <JsonStatusBadge statusBadge={lastStatusBadge as StatusBadge} />;
                }

                return null;
            }

            if (jsonResult.data_type === 'llm_log_link') {
                const llmLog = jsonResult as LLMLogLinkType;

                return <LLMLogLink sessionUid={llmLog.session_uid} />;
            }

            if (jsonResult.data_type === 'tool_selector') {
                const toolSelect = jsonResult as ToolSelect;

                return <ToolSelector tools={toolSelect.data} messageCustomService={messageCustomService} />;
            }

            if (jsonResult.data_type === 'hchart') {
                const hChartConfig = jsonResult as HChart;

                setBadgeType('UsingMCP');
                const conciergeResponse4Popup = getConciergeResponse4Popup(dialogItem, partIdx);

                if (jsonResult.data.chart_type === 'EMPTY_CHART') {
                    return null;
                }

                return <JsonHChart hChartConfig={hChartConfig} conciergeResponse={conciergeResponse4Popup} />;
            }

            if (jsonResult.data_type === 'auth-required') {
                const authRequired = jsonResult as AuthRequiredType;
                setBadgeType('AuthRequired');

                return <AuthRequired authRequired={authRequired} onRemoveMessage={() => onRemoveMessage?.(index)} />;
            }

            if (jsonResult.data_type === 'proceed-button') {
                const authRequiredToolNames: string[] = [];

                dialog.forEach((dialogItem) => {
                    dialogItem.parts.forEach((part) => {
                        if (part.type === 'json' && part.jsonBlocks) {
                            part.jsonBlocks.forEach((block) => {
                                if (block.data_type === 'auth-required') {
                                    const authBlock = block as AuthRequiredType;
                                    authBlock.data.forEach((authData) => {
                                        if (!authRequiredToolNames.includes(authData.toolName)) {
                                            authRequiredToolNames.push(authData.toolName);
                                        }
                                    });
                                }
                            });
                        }
                    });
                });

                return (
                    <ProceedButton
                        onRemoveAuthMessages={onRemoveAuthMessages}
                        authRequiredToolNames={authRequiredToolNames}
                    />
                );
            }

            if (jsonResult.data_type === 'action_buttons') {
                const actionButtons = jsonResult as ActionButtonsType;
                return <ActionButtons actionButtons={actionButtons} onAction={onAction} />;
            }

            if (jsonResult.data_type === 'stream_thinking') {
                return <StreamThinkingBlock content={jsonResult.content} loading={loading} />;
            }

            if (jsonResult.data_type === 'thinking') {
                return null;
            }

            if (jsonResult.data_type === 'clarification_question') {
                return null;
            }
        } catch (e: unknown) {
            return <Box>{e instanceof Error ? e.message : 'An error occurred'}</Box>;
        }

        return <Box>{JSON.stringify(jsonResult, null, 2)}</Box>;
    };

    const drawMessage = (parts: IMessagePart[], type: 'response' | 'request') => {
        let allPartsAreMarkdown = true;
        let markdownMessage = '';

        parts.forEach((part, index) => {
            if (part.type !== 'markdown') {
                allPartsAreMarkdown = false;
                return;
            } else {
                markdownMessage += part.message;
            }
        });

        if (allPartsAreMarkdown) {
            const hasLatex = LATEX_IN_MESSAGE_REGEX.exec(markdownMessage) !== null;
            // Reset regex position
            LATEX_IN_MESSAGE_REGEX.lastIndex = 0;

            if (hasLatex) {
                const latexParts = markdownMessage.split(LATEX_IN_MESSAGE_REGEX);
                const parts = [];

                for (let j = 0; j < latexParts.length; j++) {
                    if (j % 2 === 0) {
                        const inlineFormula: string[] = [];

                        parts.push(
                            <Box
                                sx={styles.dialogItemText.messageText}
                                key={index}
                                dangerouslySetInnerHTML={{
                                    __html: md
                                        .render(
                                            latexParts[j]
                                                .split(INLINE_LATEX_IN_MESSAGE_REGEX)
                                                .map((part, index) => {
                                                    if (index % 2 === 0) {
                                                        return part;
                                                    } else {
                                                        inlineFormula.push(
                                                            `<span class="formula-inline">
                                                            ${katex.renderToString(escapeLatexSpecial(part.trim()), {
                                                                throwOnError: false,
                                                                displayMode: false,
                                                                maxSize: 50,
                                                            })}
                                                        </span>`,
                                                        );

                                                        return `%INLINE_FORMULA_${inlineFormula.length - 1}%`;
                                                    }
                                                })
                                                .join('')
                                                .trim()
                                                .replace(MD_LINK_REGEX, MD_LINK_REPLACER)
                                                .replace(MD_NOT_FULL_LINK_REGEX, MD_NOT_FULL_LINK_REPLACER),
                                        )
                                        .replace(/%INLINE_FORMULA_(\d+)%/g, (_, index) => inlineFormula[index]),
                                }}
                            />,
                        );
                    } else {
                        parts.push(
                            <Box
                                sx={styles.formulaContainer}
                                key={index}
                                dangerouslySetInnerHTML={{
                                    __html: katex.renderToString(latexParts[j].trim(), {
                                        throwOnError: false,
                                        displayMode: false,
                                        output: 'html',
                                    }),
                                }}
                            />,
                        );
                    }
                }

                return <Box sx={styles.dialogItemText.messageText}>{parts}</Box>;
            }

            const renderedHtml = md.render(markdownMessage);
            const parsedTables = parseMarkdownTable(renderedHtml);

            if (parsedTables.length > 0) {
                const parts: React.ReactNode[] = [];

                parsedTables.forEach((tableData, idx) => {
                    if (tableData.htmlBefore) {
                        parts.push(
                            <Box
                                key={`html-${idx}`}
                                sx={styles.dialogItemText.messageText}
                                dangerouslySetInnerHTML={{
                                    __html: tableData.htmlBefore,
                                }}
                            />,
                        );
                    }

                    parts.push(
                        <JsonTable
                            key={`table-${idx}`}
                            tableData={tableData}
                            messageId={`msg-${index}-table-${idx}`}
                            conciergeResponse={null}
                        />,
                    );
                });

                const lastTable = parsedTables[parsedTables.length - 1];
                if (lastTable.htmlAfter) {
                    parts.push(
                        <Box
                            key="html-after"
                            sx={styles.dialogItemText.messageText}
                            dangerouslySetInnerHTML={{
                                __html: lastTable.htmlAfter,
                            }}
                        />,
                    );
                }

                return <Box sx={styles.dialogItemText.messageText}>{parts}</Box>;
            }

            return (
                <Box
                    sx={styles.dialogItemText.messageText}
                    key={index}
                    dangerouslySetInnerHTML={{
                        __html: renderedHtml,
                    }}
                />
            );
        } else {
            markdownMessage = '';
        }

        return (
            <Box sx={styles.dialogItemText} justifyContent={'flex-start'} alignItems={'flex-start'}>
                {parts.map((part, index) => {
                    if (part.type == 'html') {
                        if (type === 'response') {
                            if (allowJS) {
                                return <HtmlPart key={index} htmlContent={part.message} />;
                            } else {
                                return <Box key={index}>{parse(part.message)}</Box>;
                            }
                        } else {
                            const message = escapeHtml(part.message);
                            return <Box key={index}>{parse(message)}</Box>;
                        }
                    }

                    if (part.type == 'markdown') {
                        return <Box key={index}>{parse(md.render(part.message))}</Box>;
                    }

                    if (part.type == 'invalid-request') {
                        return <InvalidRequestPart key={index} message={part.message} />;
                    }

                    return <Box>{'not ready yet'}</Box>;
                })}
            </Box>
        );
    };

    const userIcon =
        customIconAvatar !== '' ? { type: 'upload', value: customIconAvatar } : { type: 'mi', value: 'user-profile' };

    const getDomainsForIcon = () => {
        const savedDomainName = messageDomains[item.uid];
        if (savedDomainName) {
            const savedDomain = availableDomains.find((d) => d.name === savedDomainName);
            if (savedDomain) {
                return [savedDomain];
            }
        }

        const domains: IDomain[] = [];

        if (messageCustomService) {
            availableDomains.forEach((domain) => {
                if (domain.customServices && domain.customServices.length > 0) {
                    const serviceExist = domain.customServices.find(
                        (service) =>
                            service.domain_id === messageCustomService.domain_id &&
                            service.name === messageCustomService.name,
                    );
                    if (serviceExist) {
                        domains.push(domain);
                    }
                }
            });

            return domains;
        }

        if (selectedDomainsOnInit.length === 1) {
            const domain = availableDomains.find((d) => d.name === selectedDomainsOnInit[0]);

            if (domain) {
                if (responseTool && domain.customServices && domain.customServices.length > 0) {
                    const serviceExist = domain.customServices.find((service) => service.name === responseTool);
                    if (serviceExist) {
                        domains.push(domain);
                    }
                } else if (!responseTool) {
                    domains.push(domain);
                }
            }

            return domains;
        }

        if (responseTool) {
            availableDomains.forEach((domain) => {
                if (domain.customServices && domain.customServices.length > 0) {
                    const serviceExist = domain.customServices.find((service) => service.name === responseTool);
                    if (serviceExist && !domains.find((d) => d.name === domain.name)) {
                        domains.push(domain);
                    }
                }
            });
        }

        return domains;
    };

    const domainsForIcon = getDomainsForIcon();

    useEffect(() => {
        if (domainsForIcon.length === 1 && !messageDomains[item.uid]) {
            dispatch(
                setMessageDomain({
                    messageUid: item.uid,
                    domainName: domainsForIcon[0].name,
                }),
            );
        }
    }, [domainsForIcon, item.uid, messageDomains, dispatch]);

    const getDocsTooltip = () => {
        const tool = availableTools.find((t) => t.name === responseTool);
        return domainsForIcon.length === 1 ? domainsForIcon[0].title : tool ? tool.title : t('badge.documentation');
    };

    const getSearchTooltip = () => {
        const tool = availableTools.find((t) => t.name === responseTool);
        const title = domainsForIcon.length === 1 ? domainsForIcon[0].title : tool ? tool.title : t('badge.search');

        return (
            <Stack spacing={1}>
                <Box>{title}</Box>
                {showSearchRequestIcon && searchParams && (
                    <>
                        <Divider />
                        <Box>{getSearchParams(searchParams)}</Box>
                    </>
                )}
            </Stack>
        );
    };

    const getToolIcon = (defaultIcon?: string) => {
        const domains = domainsForIcon;
        const tool = availableTools.find((t) => t.name === responseTool);

        if (domains.length === 1) {
            const domain = domains[0];

            if (domain.icon_ind !== 'none') {
                const iconProps = {
                    type: domain.icon_ind,
                    value:
                        domain.icon_ind === 'upload' ? `data:image/png;base64,${domain.icon_data}` : domain.icon_name,
                };

                const icon = <IconHandler icon={iconProps} sx={{ fontSize: '16px' }} />;
                return <Box sx={styles.badgeHolder}>{icon}</Box>;
            }
        }

        if (tool && tool.icon > '') {
            const icon = <IconMi icon={tool.icon} fontSize="16" />;
            return <Box sx={styles.badgeHolder}>{icon}</Box>;
        }

        if (defaultIcon) {
            const icon = <IconMi icon={defaultIcon} fontSize="16" />;
            return <Box sx={styles.badgeHolder}>{icon}</Box>;
        }

        return <Box />;
    };

    const getToolTooltip = () => {
        const domains = domainsForIcon;
        const tool = availableTools.find((t) => t.name === responseTool);

        const title = domains.length === 1 ? domains[0].title : tool ? tool.title : t('badge.mcp');

        return (
            <Stack spacing={1}>
                <Box>{title}</Box>
                {sqlParams && (
                    <>
                        <Divider />
                        <Box
                            sx={{
                                mt: 1,
                                fontSize: '0.8rem',
                                color: (theme) => alpha(theme.palette.text.primary, 0.64),
                            }}
                        >
                            {sqlParams}
                        </Box>
                    </>
                )}
            </Stack>
        );
    };

    const getClassName = () => {
        if (item.type == 'response') {
            if (summaryOnly) {
                return 'botItemSummary';
            } else {
                return 'botItem';
            }
        }

        return 'userItem';
    };

    return (
        <Stack
            key={index}
            direction={'row'}
            sx={{
                ...styles.dialogItem,
                pb: item.isClarificationQuestion || item.isClarificationAnswer ? 1 : showFeedback ? 4 : 2,
            }}
            className={getClassName()}
            onClick={
                summaryOnly
                    ? () => {
                          showInSearch();
                          setSelectedMessage(index);
                      }
                    : undefined
            }
        >
            {!isMobile && (
                <>
                    <Box sx={{ ...styles.dialogItemAvatar, position: 'relative' }}>
                        <Box sx={hasError ? styles.dialogItemAvatarUserError : styles.dialogItemAvatarUser}>
                            {hasError ? (
                                <IconMi icon="alert" sx={{ fontSize: '24px', color: 'error.main' }} />
                            ) : (
                                !showCheck && (
                                    <>
                                        {item.type == 'response' ? (
                                            <IconHandler icon={iconProps} sx={{ fontSize: '24px' }} />
                                        ) : (
                                            <IconHandler icon={userIcon} sx={{ fontSize: '24px' }} />
                                        )}
                                    </>
                                )
                            )}
                        </Box>
                        {isLastBotMessage && !hasError && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '-3px',
                                    left: '-3px',
                                    width: '30px',
                                    height: '30px',
                                    pointerEvents: 'none',
                                    '@keyframes concierge-loader-spin': {
                                        from: { transform: 'rotate(0deg)' },
                                        to: { transform: 'rotate(360deg)' },
                                    },
                                    animation: 'concierge-loader-spin 1s linear infinite',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <ConciergeLoader width="34" height="34" />
                            </Box>
                        )}
                        {showCheck && !hasError && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    pointerEvents: 'none',
                                    borderRadius: '50%',
                                    border: (theme: Theme) => `1.5px solid ${theme.palette.success.main}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxSizing: 'border-box',
                                }}
                            >
                                <IconMi icon="check" sx={{ fontSize: '14px', color: 'success.main' }} />
                            </Box>
                        )}
                    </Box>
                    {badgeType && item.type == 'response' && badgeType === 'Searching' && (
                        <Tooltip title={getSearchTooltip()}>{getToolIcon('search')}</Tooltip>
                    )}
                    {badgeType && item.type == 'response' && badgeType === 'AnalyzingDocumentation' && (
                        <Tooltip title={getDocsTooltip()}>{getToolIcon('documents')}</Tooltip>
                    )}
                    {badgeType && item.type == 'response' && badgeType === 'UsingMCP' && (
                        <Tooltip title={getToolTooltip()}>{getToolIcon()}</Tooltip>
                    )}
                    {badgeType && item.type == 'response' && badgeType === 'AuthRequired' && (
                        <Tooltip title={t('badge.auth_required')}>
                            <Box sx={styles.badgeHolder}>
                                <IconMi icon={'data-source'} fontSize="16" />
                            </Box>
                        </Tooltip>
                    )}
                </>
            )}
            <Stack sx={{ minWidth: 0, width: '100%' }}>{message}</Stack>
        </Stack>
    );
}

export default React.memo(Message);
